import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Container,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Step,
  StepButton,
  Stepper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import QrCode2OutlinedIcon from '@mui/icons-material/QrCode2Outlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import useAuth from '../../auth/useAuth';
import useCart from '../../cart/useCart';
import ordersApi from '../../api/ordersApi';
import deliveryApi from '../../api/deliveryApi';
import { getErrorMessage } from '../../utils/errorHandler';
import { CONTACT_WHATSAPP_NUMBER, PROVINCE_SHIPPING_COST, ROLES, SHIPPING_AGENCIES } from '../../config';
import QuantityStepper from '../../sections/client/products/QuantityStepper';
import LocationPicker from '../../sections/client/checkout/LocationPicker';

const formatMoney = (value, currency) =>
  new Intl.NumberFormat(currency === 'USD' ? 'en-US' : 'es-PE', { style: 'currency', currency }).format(value || 0);

const STEP_LABELS = ['Identificación', 'Opciones de entrega', 'Pago'];

// Metodos de pago del checkout: tarjeta/yape se simulan pagados de inmediato (no hay
// pasarela real integrada); contra_entrega/whatsapp quedan pendientes de coordinar
// (ver el mismo listado en el backend, StoreOrderRequest).
const PAYMENT_METHODS = [
  { id: 'tarjeta', label: 'Tarjeta', description: 'Débito o crédito, pago en línea.', icon: CreditCardOutlinedIcon },
  { id: 'yape', label: 'Yape', description: 'Yapea el total y confirma por WhatsApp.', icon: QrCode2OutlinedIcon },
  {
    id: 'contra_entrega',
    label: 'Contra entrega',
    description: 'Paga en efectivo o POS al recibir tu pedido.',
    icon: LocalShippingOutlinedIcon,
  },
  { id: 'whatsapp', label: 'Por WhatsApp', description: 'Coordina tu compra con un asesor.', icon: WhatsAppIcon },
];

const whatsAppLink = (message) => `https://wa.me/${CONTACT_WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;

// Encabezado numerado de una seccion del checkout: icono + titulo, y si ya esta completa
// (paso anterior) un boton para volver a editarla.
function StepHeader({ index, title, icon: Icon, editable, onEdit }) {
  return (
    <Stack sx={{ alignItems: 'center', justifyContent: 'space-between' }} direction="row">
      <Stack sx={{ alignItems: 'center' }} direction="row" spacing={1.5}>
        <Icon color="primary" fontSize="small" />
        <Typography variant="h6">
          {index}. {title}
        </Typography>
      </Stack>
      {editable && (
        <Button size="small" startIcon={<EditOutlinedIcon fontSize="small" />} onClick={onEdit}>
          Editar
        </Button>
      )}
    </Stack>
  );
}

// Selector de metodo de pago: tarjetas seleccionables (una sola opcion a la vez).
function PaymentMethodSelector({ value, onChange }) {
  return (
    <Grid container spacing={1.5}>
      {PAYMENT_METHODS.map((method) => {
        const Icon = method.icon;
        const selected = value === method.id;
        return (
          <Grid key={method.id} size={{ xs: 12, sm: 6 }}>
            <Card
              variant="outlined"
              sx={{
                position: 'relative',
                borderColor: selected ? 'primary.main' : 'divider',
                borderWidth: selected ? 2 : 1,
              }}
            >
              <CardActionArea onClick={() => onChange(method.id)} sx={{ p: 1.5 }}>
                {selected && (
                  <CheckCircleIcon color="primary" fontSize="small" sx={{ position: 'absolute', top: 8, right: 8 }} />
                )}
                <Stack sx={{ alignItems: 'flex-start' }} direction="row" spacing={1.5}>
                  <Icon color={selected ? 'primary' : 'action'} />
                  <Stack spacing={0.25}>
                    <Typography variant="subtitle2">{method.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {method.description}
                    </Typography>
                  </Stack>
                </Stack>
              </CardActionArea>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
}

// Selector del tipo de delivery dentro de Lima (express/normal), con el precio del
// distrito elegido. Mismo patron visual que PaymentMethodSelector.
function DeliveryTypeSelector({ district, value, onChange, expressAvailable }) {
  const options = [
    ...(expressAvailable
      ? [
          {
            id: 'express',
            label: 'Delivery Express',
            description: 'Entrega el mismo día.',
            price: district.price_express,
          },
        ]
      : []),
    {
      id: 'normal',
      label: 'Delivery Normal',
      description: 'Entrega programada, hasta 3 días.',
      price: district.price_normal,
    },
  ];

  return (
    <Grid container spacing={1.5}>
      {options.map((option) => {
        const selected = value === option.id;
        return (
          <Grid key={option.id} size={{ xs: 12, sm: 6 }}>
            <Card
              variant="outlined"
              sx={{
                position: 'relative',
                borderColor: selected ? 'primary.main' : 'divider',
                borderWidth: selected ? 2 : 1,
              }}
            >
              <CardActionArea onClick={() => onChange(option.id)} sx={{ p: 1.5 }}>
                {selected && (
                  <CheckCircleIcon color="primary" fontSize="small" sx={{ position: 'absolute', top: 8, right: 8 }} />
                )}
                <Stack spacing={0.25}>
                  <Typography variant="subtitle2">{option.label}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.description}
                  </Typography>
                  <Typography variant="subtitle2" color="primary.main" sx={{ mt: 0.5 }}>
                    {formatMoney(option.price, 'PEN')}
                  </Typography>
                </Stack>
              </CardActionArea>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
}

// Instrucciones mostradas tras registrar el pedido, segun el metodo de pago elegido.
function PaymentMethodInfo({ order }) {
  const message = `Hola, quisiera coordinar el pedido N.° ${order.id} (Total: ${formatMoney(order.total, order.currency)}).`;

  switch (order.payment_method) {
    case 'tarjeta':
      return <Alert severity="success">Tu pago fue procesado correctamente (pago simulado).</Alert>;
    case 'yape':
      return (
        <Stack spacing={1.5}>
          <Alert severity="info">
            Yapea <strong>{formatMoney(order.total, order.currency)}</strong> al número{' '}
            <strong>{CONTACT_WHATSAPP_NUMBER}</strong> y envíanos la captura para confirmar tu pedido.
          </Alert>
          <Button
            fullWidth
            variant="contained"
            color="success"
            startIcon={<WhatsAppIcon />}
            component="a"
            href={whatsAppLink(message)}
            target="_blank"
            rel="noopener noreferrer"
          >
            Enviar comprobante por WhatsApp
          </Button>
        </Stack>
      );
    case 'contra_entrega':
      return (
        <Alert severity="info">
          Paga en efectivo o con POS cuando recibas tu pedido. Te contactaremos para coordinar la entrega.
        </Alert>
      );
    case 'whatsapp':
      return (
        <Stack spacing={1.5}>
          <Alert severity="info">Continúa la compra por WhatsApp: un asesor te ayudará a coordinar el pago y la entrega.</Alert>
          <Button
            fullWidth
            variant="contained"
            color="success"
            startIcon={<WhatsAppIcon />}
            component="a"
            href={whatsAppLink(message)}
            target="_blank"
            rel="noopener noreferrer"
          >
            Continuar por WhatsApp
          </Button>
        </Stack>
      );
    default:
      return null;
  }
}

export default function CheckoutPage() {
  const { user, isAuthenticated } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const { items, updateQuantity, removeItem, clear } = useCart();

  const [selectedCurrency, setCurrency] = useState('PEN');
  const [placing, setPlacing] = useState(false);

  // El distribuidor solo compra en soles (sus precios ya vienen convertidos con su tipo de cambio).
  const isDistributor = user?.role === ROLES.DISTRIBUIDOR;
  const currency = isDistributor ? 'PEN' : selectedCurrency;
  const [order, setOrder] = useState(null);

  // Paso 1: Identificacion (nombre/correo son de solo lectura si hay sesion iniciada).
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [documentType, setDocumentType] = useState('DNI');
  const [documentNumber, setDocumentNumber] = useState('');
  const [invoiceType, setInvoiceType] = useState('boleta');

  // Paso 2: Opciones de entrega. `selectedDistrictId` es el id de un distrito de Lima con
  // costo fijo, 'otro' para un distrito fuera de la lista (costo a coordinar), o '' sin elegir aun.
  const [shippingMethod, setShippingMethod] = useState('recojo_tienda');
  const [shippingAddress, setShippingAddress] = useState('');
  const [shippingDistrict, setShippingDistrict] = useState('');
  const [shippingCity, setShippingCity] = useState('');
  const [deliveryDistricts, setDeliveryDistricts] = useState([]);
  const [selectedDistrictId, setSelectedDistrictId] = useState('');
  const [deliveryType, setDeliveryType] = useState('');
  // Punto marcado en el mapa para el envio a domicilio ({ latitude, longitude } o null).
  const [shippingLocation, setShippingLocation] = useState(null);
  // Envio a provincia: provincia de destino, agencia de transporte y su sede/agencia de destino.
  const [shippingProvince, setShippingProvince] = useState('');
  const [shippingAgency, setShippingAgency] = useState('');
  const [shippingAgencyDestination, setShippingAgencyDestination] = useState('');

  // Paso 3: Pago.
  const [paymentMethod, setPaymentMethod] = useState('');

  // activeStep: paso abierto actualmente. maxStep: el mas avanzado ya alcanzado, hasta donde
  // se puede volver a hacer clic (los pasos posteriores quedan bloqueados/grises).
  const [activeStep, setActiveStep] = useState(0);
  const [maxStep, setMaxStep] = useState(0);

  // Si hay sesion iniciada, precarga nombre/correo/telefono de la cuenta y, si existe,
  // el documento/direccion guardados en su perfil de una compra anterior.
  useEffect(() => {
    if (!user) {
      return;
    }
    setContactName(user.name || '');
    setContactEmail(user.email || '');
    setContactPhone(user.phone || '');
    if (user.profile) {
      setDocumentType(user.profile.document_type || 'DNI');
      setDocumentNumber(user.profile.document_number || '');
      if (user.profile.address) {
        setShippingMethod('delivery');
        setShippingAddress(user.profile.address || '');
        setShippingDistrict(user.profile.district || '');
        setShippingCity(user.profile.city || '');
      }
    }
  }, [user]);

  // Distritos de Lima con costo de delivery propio, para el select del paso "Envio".
  useEffect(() => {
    deliveryApi
      .getActiveDeliveryDistricts()
      .then(({ data }) => setDeliveryDistricts(data.data))
      .catch(() => {
        // Secundario: si falla, el paso 2 simplemente no ofrece distritos de Lima con costo
        // fijo y todo queda como "otro" (direccion libre, sin costo de delivery calculado).
      });
  }, []);

  const selectedDistrict = deliveryDistricts.find((d) => d.id === selectedDistrictId);
  const isOtherDistrict = selectedDistrictId === 'otro';
  const isProvinceShipping = shippingMethod === 'envio_provincia';
  // Costo de envio en soles: fijo para provincia (embalaje y traslado a la agencia), o el del
  // distrito de Lima elegido para el envio a domicilio.
  const deliveryCostPen = isProvinceShipping
    ? PROVINCE_SHIPPING_COST
    : shippingMethod === 'delivery' && selectedDistrict && deliveryType
      ? (deliveryType === 'express' ? selectedDistrict.price_express : selectedDistrict.price_normal)
      : 0;
  // Express solo se ofrece si TODOS los productos del carrito lo admiten (ver el switch
  // "Admite delivery express" en la ficha del producto); items sin el campo (carritos
  // guardados antes de esta funcion) se tratan como que si lo admiten.
  const expressAvailable = items.every((item) => item.product.express_delivery !== false);

  // Si el carrito cambia (paso 3 permite editar cantidades/quitar items) y deja de admitir
  // express con "Delivery Express" ya elegido, se vuelve a pedir el tipo de envio.
  useEffect(() => {
    if (!expressAvailable && deliveryType === 'express') {
      setDeliveryType('');
    }
  }, [expressAvailable, deliveryType]);

  const handleDistrictChange = (value) => {
    setSelectedDistrictId(value);
    setDeliveryType('');
    if (value === 'otro') {
      setShippingDistrict('');
      setShippingCity('');
    } else {
      const district = deliveryDistricts.find((d) => d.id === value);
      setShippingDistrict(district?.district || '');
      setShippingCity('Lima');
    }
  };

  const priceField = currency === 'USD' ? 'price_usd' : 'price_pen';
  const itemsTotal = items.reduce((sum, item) => sum + item.product[priceField] * item.quantity, 0);
  // El delivery siempre es en soles: si se paga en dolares se muestra/cobra aparte (no se
  // mezcla en este total), igual que hace el backend (ver OrderController).
  const total = currency === 'PEN' ? itemsTotal + deliveryCostPen : itemsTotal;

  const goToStep = (index) => {
    if (index <= maxStep) {
      setActiveStep(index);
    }
  };

  const handleContinueStep1 = () => {
    if (!contactName.trim() || !contactEmail.trim() || !contactPhone.trim() || !documentNumber.trim()) {
      enqueueSnackbar('Completa tus datos de identificación.', { variant: 'warning' });
      return;
    }
    if (invoiceType === 'factura' && documentType !== 'RUC') {
      enqueueSnackbar('Para factura, el tipo de documento debe ser RUC.', { variant: 'warning' });
      return;
    }
    setActiveStep(1);
    setMaxStep((prev) => Math.max(prev, 1));
  };

  const handleContinueStep2 = () => {
    if (shippingMethod === 'delivery') {
      if (!shippingAddress.trim() || !selectedDistrictId) {
        enqueueSnackbar('Completa la dirección y elige tu distrito.', { variant: 'warning' });
        return;
      }
      if (isOtherDistrict && (!shippingDistrict.trim() || !shippingCity.trim())) {
        enqueueSnackbar('Completa el distrito y la ciudad.', { variant: 'warning' });
        return;
      }
      if (!isOtherDistrict && !deliveryType) {
        enqueueSnackbar('Elige el tipo de delivery.', { variant: 'warning' });
        return;
      }
    }
    if (isProvinceShipping && (!shippingProvince.trim() || !shippingAgency.trim() || !shippingAgencyDestination.trim())) {
      enqueueSnackbar('Completa la provincia, la agencia de envío y la agencia de destino.', { variant: 'warning' });
      return;
    }
    setActiveStep(2);
    setMaxStep((prev) => Math.max(prev, 2));
  };

  const handlePlaceOrder = async () => {
    if (items.length === 0) {
      enqueueSnackbar('Agrega al menos un producto a tu compra.', { variant: 'warning' });
      return;
    }
    if (!paymentMethod) {
      enqueueSnackbar('Elige un método de pago.', { variant: 'warning' });
      return;
    }
    setPlacing(true);
    try {
      const { data } = await ordersApi.createOrder({
        currency,
        payment_method: paymentMethod,
        items: items.map((item) => ({ product_id: item.product.id, quantity: item.quantity })),
        phone: contactPhone,
        document_type: documentType,
        document_number: documentNumber,
        invoice_type: invoiceType,
        shipping_method: shippingMethod,
        ...(shippingMethod === 'delivery' && {
          shipping_address: shippingAddress,
          shipping_district: shippingDistrict,
          shipping_city: shippingCity,
          ...(shippingLocation && {
            shipping_latitude: shippingLocation.latitude,
            shipping_longitude: shippingLocation.longitude,
          }),
        }),
        ...(isProvinceShipping && {
          shipping_province: shippingProvince,
          shipping_agency: shippingAgency,
          shipping_agency_destination: shippingAgencyDestination,
        }),
        ...(shippingMethod === 'delivery' && !isOtherDistrict && selectedDistrictId && {
          delivery_district_id: selectedDistrictId,
          delivery_type: deliveryType,
        }),
        ...(!isAuthenticated && {
          customer_name: contactName,
          customer_email: contactEmail,
        }),
      });
      setOrder(data.data);
      clear();
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo procesar tu compra.'), { variant: 'error' });
    } finally {
      setPlacing(false);
    }
  };

  if (order) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Card>
          <CardContent>
            <Stack spacing={1} sx={{ textAlign: 'center', mb: 3 }}>
              <Typography variant="h5">¡Pedido registrado!</Typography>
              <Typography color="text.secondary">
                Pedido N.° {order.id} · Total: {formatMoney(order.total, order.currency)}
              </Typography>
            </Stack>

            {order.shipping_method === 'envio_provincia' && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Envío a provincia ({order.shipping_province}) por {order.shipping_agency} · agencia de destino:{' '}
                {order.shipping_agency_destination}. Embalaje y envío a la agencia:{' '}
                {formatMoney(order.delivery_cost, 'PEN')}
                {order.currency === 'USD' && ' — se paga aparte, no está incluido en el total en US$'}. El flete de la
                agencia se paga al recoger el pedido.
              </Alert>
            )}

            {order.delivery_type && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Delivery {order.delivery_type === 'express' ? 'Express (mismo día)' : 'Normal (hasta 3 días)'}:{' '}
                {formatMoney(order.delivery_cost, 'PEN')}
                {order.currency === 'USD' && ' — se paga aparte, no está incluido en el total en US$.'}
              </Alert>
            )}

            <PaymentMethodInfo order={order} />

            <Button fullWidth sx={{ mt: 3 }} onClick={() => setOrder(null)}>
              Realizar otra compra
            </Button>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 5 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Checkout
      </Typography>

      <Stepper nonLinear activeStep={activeStep} sx={{ mb: 4 }}>
        {STEP_LABELS.map((label, index) => (
          <Step key={label} completed={index < maxStep}>
            <StepButton disabled={index > maxStep} onClick={() => goToStep(index)}>
              {label}
            </StepButton>
          </Step>
        ))}
      </Stepper>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Stack spacing={3}>
            {/* Paso 1: Identificacion */}
            <Card variant={activeStep === 0 ? 'elevation' : 'outlined'}>
              <CardContent>
                <StepHeader
                  index={1}
                  title="Identificación"
                  icon={CheckCircleIcon}
                  editable={activeStep !== 0 && maxStep >= 1}
                  onEdit={() => goToStep(0)}
                />

                {activeStep === 0 ? (
                  <Stack spacing={2} sx={{ mt: 2 }}>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          label="Correo electrónico"
                          type="email"
                          fullWidth
                          required
                          disabled={isAuthenticated}
                          value={contactEmail}
                          onChange={(e) => setContactEmail(e.target.value)}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          label="Teléfono"
                          fullWidth
                          required
                          value={contactPhone}
                          onChange={(e) => setContactPhone(e.target.value)}
                        />
                      </Grid>
                      <Grid size={12}>
                        <TextField
                          label="Nombre completo"
                          fullWidth
                          required
                          disabled={isAuthenticated}
                          value={contactName}
                          onChange={(e) => setContactName(e.target.value)}
                        />
                      </Grid>
                      <Grid size={{ xs: 6, sm: 4 }}>
                        <TextField
                          select
                          label="Tipo de documento"
                          fullWidth
                          value={documentType}
                          onChange={(e) => setDocumentType(e.target.value)}
                        >
                          <MenuItem value="DNI">DNI</MenuItem>
                          <MenuItem value="RUC">RUC</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid size={{ xs: 6, sm: 4 }}>
                        <TextField
                          label="Nro. de documento"
                          fullWidth
                          required
                          value={documentNumber}
                          onChange={(e) => setDocumentNumber(e.target.value)}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <TextField
                          select
                          label="Tipo de comprobante"
                          fullWidth
                          value={invoiceType}
                          onChange={(e) => setInvoiceType(e.target.value)}
                        >
                          <MenuItem value="boleta">Boleta</MenuItem>
                          <MenuItem value="factura">Factura</MenuItem>
                        </TextField>
                      </Grid>
                    </Grid>
                    <Button variant="contained" onClick={handleContinueStep1} sx={{ alignSelf: 'flex-start' }}>
                      Continuar
                    </Button>
                  </Stack>
                ) : (
                  maxStep >= 1 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {contactName} · {contactEmail} · {documentType} {documentNumber}
                    </Typography>
                  )
                )}
              </CardContent>
            </Card>

            {/* Paso 2: Opciones de entrega */}
            <Card variant={activeStep === 1 ? 'elevation' : 'outlined'} sx={{ opacity: maxStep >= 1 ? 1 : 0.5 }}>
              <CardContent>
                <StepHeader
                  index={2}
                  title="Opciones de entrega"
                  icon={LocalShippingOutlinedIcon}
                  editable={activeStep !== 1 && maxStep >= 2}
                  onEdit={() => goToStep(1)}
                />

                {activeStep === 1 ? (
                  <Stack spacing={2} sx={{ mt: 2 }}>
                    <ToggleButtonGroup
                      exclusive
                      value={shippingMethod}
                      onChange={(e, value) => value && setShippingMethod(value)}
                      sx={{ flexWrap: 'wrap' }}
                    >
                      <ToggleButton value="recojo_tienda">Recojo en tienda</ToggleButton>
                      <ToggleButton value="delivery">Envío a domicilio</ToggleButton>
                      <ToggleButton value="envio_provincia">Envío a provincia</ToggleButton>
                    </ToggleButtonGroup>

                    {shippingMethod === 'delivery' && (
                      <Stack spacing={2}>
                        <TextField
                          label="Dirección"
                          fullWidth
                          required
                          value={shippingAddress}
                          onChange={(e) => setShippingAddress(e.target.value)}
                        />

                        <TextField
                          select
                          label="Distrito"
                          fullWidth
                          required
                          value={selectedDistrictId}
                          onChange={(e) => handleDistrictChange(e.target.value)}
                        >
                          <MenuItem value="" disabled>
                            Selecciona tu distrito
                          </MenuItem>
                          {deliveryDistricts.map((district) => (
                            <MenuItem key={district.id} value={district.id}>
                              {district.district}
                            </MenuItem>
                          ))}
                          <MenuItem value="otro">Otro distrito</MenuItem>
                        </TextField>

                        {isOtherDistrict ? (
                          <>
                            <Grid container spacing={2}>
                              <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                  label="Distrito"
                                  fullWidth
                                  required
                                  value={shippingDistrict}
                                  onChange={(e) => setShippingDistrict(e.target.value)}
                                />
                              </Grid>
                              <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                  label="Ciudad"
                                  fullWidth
                                  required
                                  value={shippingCity}
                                  onChange={(e) => setShippingCity(e.target.value)}
                                />
                              </Grid>
                            </Grid>
                            <Alert severity="info">
                              El costo de delivery a este distrito se coordina al confirmar tu pedido. Si estás fuera
                              de Lima, elige "Envío a provincia".
                            </Alert>
                          </>
                        ) : (
                          selectedDistrict && (
                            <Stack spacing={1}>
                              <DeliveryTypeSelector
                                district={selectedDistrict}
                                value={deliveryType}
                                onChange={setDeliveryType}
                                expressAvailable={expressAvailable}
                              />
                              {!expressAvailable && (
                                <Alert severity="info">
                                  Uno o más productos de tu carrito no admiten delivery express; tu pedido se
                                  enviará por delivery normal.
                                </Alert>
                              )}
                            </Stack>
                          )
                        )}

                        <LocationPicker value={shippingLocation} onChange={setShippingLocation} />
                      </Stack>
                    )}

                    {isProvinceShipping && (
                      <Stack spacing={2}>
                        <Alert severity="info">
                          Costo de embalaje y envío a la agencia:{' '}
                          <strong>{formatMoney(PROVINCE_SHIPPING_COST, 'PEN')}</strong>. El flete de la agencia hasta
                          tu provincia lo pagas al recoger tu pedido.
                        </Alert>
                        <TextField
                          label="Provincia a enviar"
                          placeholder="Ej. Arequipa"
                          fullWidth
                          required
                          value={shippingProvince}
                          onChange={(e) => setShippingProvince(e.target.value)}
                        />
                        <Grid container spacing={2}>
                          <Grid size={{ xs: 12, sm: 5 }}>
                            <TextField
                              select
                              label="Agencia de envío"
                              fullWidth
                              required
                              value={shippingAgency}
                              onChange={(e) => setShippingAgency(e.target.value)}
                            >
                              {SHIPPING_AGENCIES.map((agency) => (
                                <MenuItem key={agency} value={agency}>
                                  {agency}
                                </MenuItem>
                              ))}
                              <MenuItem value="Otra">Otra (indícala en el destino)</MenuItem>
                            </TextField>
                          </Grid>
                          <Grid size={{ xs: 12, sm: 7 }}>
                            <TextField
                              label="Agencia de destino"
                              placeholder="Sede o dirección de la agencia donde recogerás"
                              fullWidth
                              required
                              value={shippingAgencyDestination}
                              onChange={(e) => setShippingAgencyDestination(e.target.value)}
                            />
                          </Grid>
                        </Grid>
                      </Stack>
                    )}

                    <Button variant="contained" onClick={handleContinueStep2} sx={{ alignSelf: 'flex-start' }}>
                      Continuar
                    </Button>
                  </Stack>
                ) : (
                  maxStep >= 2 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {shippingMethod === 'delivery'
                        ? `Envío a domicilio · ${shippingAddress}, ${shippingDistrict}, ${shippingCity}${
                            deliveryType ? ` · ${deliveryType === 'express' ? 'Express' : 'Normal'}` : ''
                          }${shippingLocation ? ' · ubicación marcada en el mapa' : ''}`
                        : isProvinceShipping
                          ? `Envío a provincia · ${shippingProvince} · ${shippingAgency} - ${shippingAgencyDestination}`
                          : 'Recojo en tienda'}
                    </Typography>
                  )
                )}
              </CardContent>
            </Card>

            {/* Paso 3: Pago */}
            <Card variant={activeStep === 2 ? 'elevation' : 'outlined'} sx={{ opacity: maxStep >= 2 ? 1 : 0.5 }}>
              <CardContent>
                <StepHeader index={3} title="Pago" icon={CreditCardOutlinedIcon} editable={false} onEdit={() => {}} />

                {activeStep === 2 && (
                  <Box sx={{ mt: 2 }}>
                    <PaymentMethodSelector value={paymentMethod} onChange={setPaymentMethod} />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Stack>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          <Card sx={{ position: { md: 'sticky' }, top: { md: 88 } }}>
            <CardContent>
              <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Resumen del pedido</Typography>
                {!isDistributor && (
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={currency}
                    onChange={(e, value) => value && setCurrency(value)}
                  >
                    <ToggleButton value="PEN">Soles</ToggleButton>
                    <ToggleButton value="USD">Dólares</ToggleButton>
                  </ToggleButtonGroup>
                )}
              </Stack>

              {items.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Aún no agregaste productos.
                </Typography>
              ) : (
                <Stack divider={<Divider />} spacing={1.5}>
                  {items.map((item) => (
                    <Stack sx={{ alignItems: 'center' }} key={item.product.id} direction="row" spacing={2}>
                      <Stack sx={{ flexGrow: 1 }}>
                        <Typography variant="body2">{item.product.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatMoney(item.product[priceField], currency)} c/u
                        </Typography>
                      </Stack>
                      <QuantityStepper
                        value={item.quantity}
                        onChange={(quantity) => updateQuantity(item.product.id, quantity)}
                      />
                      <IconButton onClick={() => removeItem(item.product.id)} size="small">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  ))}
                </Stack>
              )}

              {items.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />

                  {deliveryCostPen > 0 && (
                    <Stack direction="row" sx={{ justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {isProvinceShipping
                          ? 'Embalaje y envío a la agencia'
                          : `Delivery (${deliveryType === 'express' ? 'Express' : 'Normal'})`}
                        {currency === 'USD' && ' — se paga aparte'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {formatMoney(deliveryCostPen, 'PEN')}
                      </Typography>
                    </Stack>
                  )}

                  <Stack direction="row" sx={{ justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6">Total (IGV incluido)</Typography>
                    <Typography variant="h6">{formatMoney(total, currency)}</Typography>
                  </Stack>
                </>
              )}

              {items.length === 0 && <Alert severity="info">Agrega productos para continuar.</Alert>}

              {activeStep === 2 && items.length > 0 && (
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handlePlaceOrder}
                  disabled={placing}
                  sx={{ mt: 1 }}
                >
                  {placing ? 'Procesando...' : `Pagar ${formatMoney(total, currency)}`}
                </Button>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}
